import {isEmpty, map} from 'lodash';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {billingPlatformApi} from 'api';
import {ActionButton, Card, ConfirmModal, DataWrapper, Icon, PredefinedNotifierType, Tooltip} from 'components';
import {PaymentSourceRole} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {BillingPlatformPaymentSource} from 'types';

import {AddPaymentSourceModal} from './AddPaymentSourceModal';
import {PaymentSource} from './PaymentSource';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.BILLING.PAYMENT_SOURCE;

type Props = {
    hasWarning?: boolean;
    primaryPaymentSourceId: string;
    paymentSources: BillingPlatformPaymentSource[];
    onChange: () => void;
}

export const PaymentSources: React.FC<Props> = ({primaryPaymentSourceId, hasWarning, paymentSources, onChange}) => {
    const {t} = useTranslation();

    const [confirmDeleteModalProps, setConfirmDeleteModalProps] = useState<{
        open: boolean;
        paymentId?: string;
    }>({open: false});
    const [addPaymentModalOpen, setAddPaymentModalOpen] = useState(false);

    const onRemoveFinally = useCallback(() => {
        setConfirmDeleteModalProps({open: false});
    }, []);

    const {
        send: removePaymentSource,
        loading: removePaymentSourceLoading
    } = useApiData(billingPlatformApi.removePaymentSource, {
        onSuccess: onChange,
        onFinally: onRemoveFinally,
        notificationType: PredefinedNotifierType.Delete
    });

    const {
        send: makePaymentSourcePrimary,
        loading: makePaymentSourcePrimaryLoading
    } = useApiData(billingPlatformApi.updatePaymentSourceRole, {
        onSuccess: onChange,
        notificationType: PredefinedNotifierType.Save
    });

    const handleConfirmDeleteModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            removePaymentSource(confirmDeleteModalProps.paymentId);
        } else {
            setConfirmDeleteModalProps({open: false});
        }
    }, [confirmDeleteModalProps, removePaymentSource]);

    const handleAddPaymentModalClose = useCallback((success: boolean) => {
        if (success) {
            onChange();
        }
        setAddPaymentModalOpen(false);
    }, [onChange]);

    const onRemove = useCallback((paymentId: string) => {
        setConfirmDeleteModalProps({open: true, paymentId});
    }, []);

    const onMakePrimary = useCallback((paymentId: string) => {
        makePaymentSourcePrimary(paymentId, PaymentSourceRole.Primary);
    }, [makePaymentSourcePrimary]);

    return (
        <Card className='tw-mb-4 tw-relative'>
            <div className='tw-flex tw-items-center tw-mb-4'>
                <div className='tw-ts-h3-bold -md:tw-ts-h5-bold'>{t(T_KEYS.TITLE)}</div>
                {hasWarning &&
                    <Tooltip header={t(T_KEYS.BLOCK_ACCOUNT_WARNING)}>
                        <Icon name='alert-triangle' className='tw-ml-2' color='warning' />
                    </Tooltip>}
                <ActionButton
                    action='create'
                    className='tw-ml-auto'
                    width='small'
                    onClick={() => setAddPaymentModalOpen(true)}
                >
                    {t(T_KEYS.ADD_CARD)}
                </ActionButton>
            </div>

            <DataWrapper
                noData={isEmpty(paymentSources)}
                isLoading={makePaymentSourcePrimaryLoading}
                noDataPlaceholder={t(T_KEYS.NO_DATA_PLACEHOLDER)}
                hideContentOnLoading={false}
            >
                {map(paymentSources, (source, index) => (
                    <PaymentSource
                        key={index}
                        isPrimary={source.id === primaryPaymentSourceId}
                        paymentSource={source}
                        removePaymentSource={onRemove}
                        makePrimaryPaymentSource={onMakePrimary}
                    />
                ))}
            </DataWrapper>
            <ConfirmModal
                {...confirmDeleteModalProps}
                okDisabled={removePaymentSourceLoading}
                title={t(T_KEYS.CONFIRM_DELETE_MODAL.TITLE)}
                content={t(T_KEYS.CONFIRM_DELETE_MODAL.MESSAGE)}
                onClose={handleConfirmDeleteModalClose}
            />
            <AddPaymentSourceModal
                open={addPaymentModalOpen}
                onClose={handleAddPaymentModalClose}
            />
        </Card>
    );
};